import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactBirthdaysToday, type ContactBirthdayDto } from '../api/contacts';

interface Props {
  userId?: string;
  onOpenContact: (userId: string) => void;
}

function dismissKey(userId: string | undefined): string {
  const today = new Date().toISOString().slice(0, 10);
  return `hc_birthday_dismissed:${userId ?? 'anon'}:${today}`;
}

/**
 * Баннер "у контакта сегодня день рождения" между табами папок и списком чатов (см. ChatList).
 * По образцу ConnectingBadge — простой, сам решает, показываться ли: пусто, если у
 * просматривающего выключены уведомления (проверяется и на бэкенде, см.
 * ContactService.birthdaysToday) или баннер уже закрыт сегодня (localStorage). Закрытие —
 * на весь сегодняшний день, не персистентно на сервере: это просто "не мозоль глаза сейчас",
 * а не настройка.
 */
export default function BirthdayBanner({ userId, onOpenContact }: Props) {
  const { t } = useTranslation('misc');
  const [people, setPeople] = useState<ContactBirthdayDto[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(dismissKey(userId)) === '1');
    contactBirthdaysToday()
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [userId]);

  if (dismissed || people.length === 0) return null;

  const dismiss = () => {
    localStorage.setItem(dismissKey(userId), '1');
    setDismissed(true);
  };

  return (
    <div className="birthday-banner">
      <span className="birthday-banner-emoji">🎂</span>
      <div className="birthday-banner-text">
        {people.length === 1 ? (
          <>
            {t('birthdaySinglePrefix')}{' '}
            <span className="birthday-banner-link" onClick={() => onOpenContact(people[0].userId)}>
              {people[0].displayName}
            </span>{' '}
            {t('birthdaySingleSuffix')}
          </>
        ) : (
          <>
            {t('birthdayMultiplePrefix')}{' '}
            {people.map((p, i) => (
              <span key={p.userId}>
                <span className="birthday-banner-link" onClick={() => onOpenContact(p.userId)}>
                  {p.displayName}
                </span>
                {i < people.length - 1 ? ', ' : ''}
              </span>
            ))}
          </>
        )}
      </div>
      <button className="birthday-banner-close" title={t('birthdayClose')} onClick={dismiss}>×</button>
    </div>
  );
}
